import Link from "next/link";
import { useRouter } from "next/router";
import * as React from "react";
import { cs } from "../../core/classSet";
import { getIndustrySlug, getTopicSlug } from "../../core/getParam";
import { useBooleanState } from "../../core/hooks/useBooleanState";
import { useTabletLargeMediaQuery } from "../../core/hooks/useMediaQuery";
import { IProcedureSectionType } from "../../graphql/gpl-graphQLTypes.gen";
import { IProceduresSummary } from "../../typings/filterEntities";
import Heading from "../Heading/Heading";
import { ArrowIcon, MinusIcon, PlusIcon } from "../Icons/Icons";
import ProcedureCard from "../ProcedureCard/ProcedureCard";
import StructuredDataProcedure from "../StructuredData/ItemList";
import styles from "./ProcedureSectionsGrid.module.css";

function ProcedureSection({ data, label, slug, totalCount, type }: IProceduresSummary) {
  const [isExpanded, isExpandedTrue, , toggleExpand] = useBooleanState();
  React.useEffect(() => {
    if (slug === "manufacturing") {
      isExpandedTrue();
    }
  }, [isExpandedTrue, slug]);

  const isTabletLargeViewport = useTabletLargeMediaQuery();
  const router = useRouter();
  const industrySlug = getIndustrySlug(router);
  const topicSlug = getTopicSlug(router);

  function getSectionHref() {
    if (type === IProcedureSectionType.Industry && topicSlug !== "all") {
      return `/${slug}/${topicSlug}`;
    }
    if (type === IProcedureSectionType.Topic) {
      return `/${industrySlug}/${slug}`;
    }
    return `/${slug}`;
  }

  return (
    <>
      <div
        className={cs(
          styles.sectionHeader,
          !isTabletLargeViewport && styles.clickable,
          !isTabletLargeViewport && !isExpanded && styles.collapsed,
        )}
        onClick={isTabletLargeViewport ? undefined : toggleExpand}
      >
        <h3 className={styles.sectionLabel}>
          {label} <span className={styles.count}>• {totalCount} procedures</span>
        </h3>
        <div className={styles.expandIcon}>{!isExpanded ? <PlusIcon /> : <MinusIcon />}</div>
        <div className={styles.tabletSeeAllWrapper}>
          <Link href={getSectionHref()}>
            See all{type === IProcedureSectionType.Topic && <> {label}</>} <ArrowIcon />
          </Link>
        </div>
      </div>
      <div className={isExpanded || isTabletLargeViewport ? undefined : styles.proceduresCollapsed}>
        <ul className={styles.procedureGrid}>
          {data.map((p) => (
            <li className={styles.procedureGridItem} key={p.uid}>
              <ProcedureCard procedure={p} />
            </li>
          ))}
        </ul>
        <div className={styles.mobileSeeAllWrapper}>
          <Link href={getSectionHref()} className={styles.mobileSeeAll}>
            See all{type === IProcedureSectionType.Topic && <> {label}</>} <ArrowIcon />
          </Link>
        </div>
      </div>
    </>
  );
}

interface IProps {
  isHomePage?: boolean;
  sections: IProceduresSummary[];
  title: string;
}

export default function ProcedureSectionsGrid({ isHomePage, sections, title }: IProps) {
  const procedures = sections.map((section) => section.data).flat();
  return (
    <section>
      <StructuredDataProcedure procedures={procedures} />
      <Heading className={styles.title} component={isHomePage ? "h2" : "h1"} variant="h3">
        {title}
      </Heading>
      <ul className={styles.sectionsGrid}>
        {sections.map((s) => (
          <li className={styles.sectionsGridItem} key={s.label}>
            <ProcedureSection
              data={s.data}
              label={s.label}
              slug={s.slug}
              totalCount={s.totalCount}
              type={s.type}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
